import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, filter, from, map, of, switchMap } from 'rxjs';

import type { Permission } from '@hunting-nature/shared-core/types/permission.type';
import { PermissionValues } from '@hunting-nature/shared-core/types/permission.type';

import { SettingsService } from './settings.service';
import { RestService } from './rest-service';

import type { HuntingProduct } from '../../pages/hunting-products/redux/hunting-products.reducer';
import { deriveHardwareId } from '../../shared/utils/derive-hardware-id.util';

/**
 * Resolved ownership data returned by {@link CannesOwnershipService.resolveOwnership$}.
 *
 * `hardwareId` and `permission` are optional because some devices may not have either
 * (e.g., products with no serialNumber yet, or REST failures that fall back to an
 * unauthenticated resolution).
 */
export interface CannesOwnershipResolution {
  deviceId: string;
  hardwareId?: string;
  permission?: Permission;
}

/**
 * Service for resolving and persisting Cannes device ownership.
 *
 * Ownership is determined by:
 * 1. Whether the product already has a `permission` cached locally
 * 2. Whether a `hardwareId` can be derived from modelName + serialNumber
 * 3. A REST call to `/cameras` to look up the user's permission for that hardwareId
 *
 * This service is the single source of truth for the ownership resolution pipeline —
 * effects only dispatch the resulting action, they do not implement the resolution logic.
 */
@Injectable({
  providedIn: 'root',
})
export class CannesOwnershipService {
  private readonly settingsService = inject(SettingsService);
  private readonly restService = inject(RestService);

  /**
   * Resolves the ownership status for a given device.
   *
   * Resolution order:
   * 1. If `deviceId` is null or no paired product found → returns `null` (caller should close prompt)
   * 2. If the product already has a `permission` → resolve with existing permission
   * 3. If no `hardwareId` can be derived → resolve without hardwareId (caller may show "no access")
   * 4. Otherwise, fetch camera permissions via REST and resolve with the found permission
   * 5. On REST failure, resolve without permission (graceful degradation)
   *
   * The cached-permission short-circuit (2) is load-bearing, not an optimization: neither the
   * Secacam `/cameras` endpoint this REST call hits, nor the Ulm device backend, expose any way to
   * re-verify a Cannes device's ownership after registration. With local pairing data scoped per
   * account, a stale cache can only go wrong if ownership changes through a channel outside this
   * app entirely.
   */
  public resolveOwnership$(deviceId: string | null, pairedProducts: Array<HuntingProduct>): Observable<CannesOwnershipResolution | null> {
    if (!deviceId) {
      return of(null);
    }

    const product = pairedProducts.find(entry => entry.deviceId === deviceId);
    if (!product) {
      return of(null);
    }

    const hardwareId = product.hardwareId ?? deriveHardwareId(product.modelName ?? null, product.serialNumber ?? null);

    if (product.permission) {
      return of({ deviceId, hardwareId, permission: product.permission });
    }

    if (!hardwareId) {
      return of({ deviceId });
    }

    return this.restService.getCamerasUserCanAccess$().pipe(
      map(response => response.cameras.find(camera => camera.id === hardwareId || camera.imei === hardwareId)?.permission),
      map(permission => ({ deviceId, hardwareId, permission })),
      catchError((error: unknown) => {
        console.error('[CannesOwnershipService] Failed to resolve ownership via REST', error);
        return of({ deviceId, hardwareId } as CannesOwnershipResolution);
      })
    );
  }

  /**
   * Persists the ownership resolution (hardwareId and/or permission) back to the
   * product in local storage.
   *
   * Only patches fields that are provided — `undefined` values are silently ignored
   * to avoid overwriting existing data with empty values.
   */
  public persistOwnershipResolution$(deviceId: string, hardwareId?: string, permission?: Permission): Observable<void> {
    return from(this.settingsService.getHuntingProduct$(deviceId)).pipe(
      filter((product): product is HuntingProduct => product !== undefined),
      switchMap(product =>
        from(
          this.settingsService.setHuntingProduct$({
            ...product,
            ...(hardwareId ? { hardwareId } : {}),
            ...(permission ? { permission } : {}),
          })
        )
      ),
      catchError((error: unknown) => {
        console.error('[CannesOwnershipService] Failed to persist ownership resolution', error);
        return of(undefined as void);
      })
    );
  }

  /**
   * Maps a successful device registration to ownership resolution data.
   *
   * @returns The ownership payload to dispatch, or `null` if the caller should
   *          close the ownership prompt instead (no deviceId available).
   */
  public mapRegistrationSuccessToOwnership$(hardwareId: string, deviceId?: string, pairedProducts?: Array<HuntingProduct>): CannesOwnershipResolution | null {
    if (!deviceId) {
      return null;
    }

    const product = pairedProducts?.find(entry => entry.deviceId === deviceId);
    return {
      deviceId,
      hardwareId: hardwareId ?? product?.hardwareId,
      permission: PermissionValues.OWNER,
    };
  }
}
